





















import Vue from 'vue'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import ThirdPartyFormInfo from '@/modules/ThirdParty/components/forms/ThirdPartyFormInfo.vue'
import { newThirdParty, editThirdParty } from '@/modules/ThirdParty/gql/third-party.gql'
import { ThirdPartyInfoInput } from '@/modules/ThirdParty/interfaces/third-party-form'
import { ThirdParty } from '@/modules/ThirdParty/interfaces/third-party'
import { states } from '@/utils/us_states'
import delNonNumCode from '@/utils/delNonNumCode'

@Component({
  components: {
    ThirdPartyFormInfo,
    AppDialog,
    AppSteps
  }
})
export default class ThirdPartyForm extends Vue {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop(String) readonly title!: string
  @Prop({ type: Object as () => ThirdParty }) readonly ThirdParty!: ThirdParty

  showDialog = false

  loading = false

  stepIndex = 0
  steps = [
    ['Information', 770, 470]
  ]

  infoInput: ThirdPartyInfoInput = {
    name: '',
    phone: '',
    fax: '',
    email: '',
    notes: ''
  }

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get showBackBtn () {
    return this.stepIndex > 0
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.ThirdParty
        ? 'Save'
        : 'Create'
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.initInput()
    }
    this.showDialog = val
  }

  resetStep () {
    this.stepIndex = this.step
  }

  initInput () {
    if (this.ThirdParty) {
      const {
        name,
        phone, phoneCountryCode, phoneCountryCallingCode, phoneExtensionNumber,
        fax, faxCountryCode, faxCountryCallingCode, faxExtensionNumber,
        email, notes
      } = this.ThirdParty

      this.infoInput = {
        name,
        phone: (phoneCountryCode || '') + ',' + (phone || '') + ',' + (phoneExtensionNumber || '') + ',' + (phoneCountryCallingCode || ''),
        fax: (faxCountryCode || '') + ',' + (fax || '') + ',' + (faxExtensionNumber || '') + ',' + (faxCountryCallingCode || ''),
        email,
        notes
      }
    } else {
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.ThirdParty) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      this.loading = true
      if (this.ThirdParty) {
        this.handleUpdate()
      } else {
        this.handleCreate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  handleBack () {
    if (this.stepIndex > 0) {
      this.stepIndex -= 1
    }
  }

  async handleCreate () {
    const {
      infoInput: {
        name,
        phone, fax, email, notes
      }
    } = this

    const phoneArr = phone.split(',')
    const faxArr = fax.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: newThirdParty,
        variables: {
          thirdPartyInput: {
            name,
            fax: delNonNumCode(faxArr[1] || ''),
            phone: delNonNumCode(phoneArr[1] || ''),
            phoneExtensionNumber: phoneArr[2] || '',
            phoneCountryCode: phoneArr[0] || '',
            phoneCountryCallingCode: phoneArr[3] || '',
            faxExtensionNumber: faxArr[2] || '',
            faxCountryCode: faxArr[0] || '',
            faxCountryCallingCode: faxArr[3] || '',
            email,
            notes
          }
        }
      })
      this.clearForm()
      this.onSuccess('Create the new third parties successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdate () {
    const {
      infoInput: {
        name,
        phone, fax, email, notes
      }
    } = this

    const phoneArr = phone.split(',')
    const faxArr = fax.split(',')

    try {
      const res = await this.$apollo.mutate({
        mutation: editThirdParty,
        variables: {
          thirdPartyId: +this.ThirdParty.id,
          thirdPartyInput: {
            name,
            fax: delNonNumCode(faxArr[1] || ''),
            phone: delNonNumCode(phoneArr[1] || ''),
            phoneExtensionNumber: phoneArr[2] || '',
            phoneCountryCode: phoneArr[0] || '',
            phoneCountryCallingCode: phoneArr[3] || '',
            faxExtensionNumber: faxArr[2] || '',
            faxCountryCode: faxArr[0] || '',
            faxCountryCallingCode: faxArr[3] || '',
            email,
            notes
          }
        }
      })
      this.clearForm()
      this.onSuccess('Update third parties successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.infoInput = {
      name: '',
      phone: '',
      fax: '',
      email: '',
      notes: ''
    }
  }

  @Emit('hide')
  handleHide () {
    this.$emit('input', false)
  }

  @Emit('cancel')
  handleCancel () {
    this.handleHide()
    this.clearForm()
  }

  @Emit('success')
  @Emit('hide')
  onSuccess (msg = '') {
    this.handleHide()
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
  }
}
